/**
 * sessionSync.ts
 *
 * Watches the Zustand booking slice and debounces writes to AppSync.
 *
 * The loop problem: sync fires → AppSync mutation → subscription pushes the
 * update back → hydrateFromSession updates the same store fields → sync fires
 * again → infinite loop.
 *
 * Fix: suppressSync flag. The subscription manager calls suppressNextSync()
 * before hydrating from the server so we skip that store change.
 */

import { generateClient } from 'aws-amplify/api';
import { shallow } from 'zustand/shallow';
import { UPDATE_BOOKING_SESSION } from '@/lib/graphql/mutations';
import { useStore } from '@/store';
import { SessionPatch } from '@airline-demo/types';

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let isSubscribed = false;
let _suppressSync = false;
let _currentSessionId: string | null = null;
let _inflightSync: Promise<void> | null = null;

// Called by subscriptionManager before applying a server-pushed update
export function suppressNextSync() {
  _suppressSync = true;
}

// Force any pending/inflight sync to complete and wait for it. Used before
// confirmBooking so the mutation doesn't race against the 300ms debounce.
export async function flushSessionSync(): Promise<void> {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (_inflightSync) await _inflightSync;
  if (_currentSessionId) {
    const p = syncToBackend(_currentSessionId).finally(() => { if (_inflightSync === p) _inflightSync = null; });
    _inflightSync = p;
    await p;
  }
}

// Lazy: generateClient() must be called after Amplify.configure()
let _client: { graphql: Function } | null = null;
const getClient = () => {
  if (!_client) _client = generateClient();
  return _client;
};

async function syncToBackend(sessionId: string) {
  const store = useStore.getState();
  store.setSyncing(true);

  const patch: SessionPatch = {
    currentStep: store.currentStep,
    status: store.status,
  };

  if (store.searchParams) patch.searchParams = store.searchParams;
  // AWSJSON scalar requires a JSON string, not a plain object
  if (store.selectedFlight) (patch as Record<string, unknown>).selectedFlight = JSON.stringify(store.selectedFlight);
  patch.selectedSeats = store.selectedSeats;
  if (store.passengers.length > 0) patch.passengers = store.passengers;
  if (store.payment) patch.paymentIntentId = store.payment.intentId;

  try {
    await (getClient().graphql({
      query: UPDATE_BOOKING_SESSION,
      variables: { sessionId, patch },
    }) as Promise<unknown>);
  } catch (err) {
    console.error('[sessionSync] mutation failed:', err);
    useStore.getState().setError('Failed to save progress. Retrying...');
  } finally {
    useStore.getState().setSyncing(false);
  }
}

export function startSessionSync(sessionId: string): () => void {
  if (isSubscribed) return () => {};
  isSubscribed = true;
  _currentSessionId = sessionId;

  const unsub = useStore.subscribe(
    (state) => ({
      currentStep: state.currentStep,
      status: state.status,
      searchParams: state.searchParams,
      selectedFlight: state.selectedFlight,
      selectedSeats: state.selectedSeats,
      passengers: state.passengers,
      payment: state.payment,
    }),
    () => {
      // If this store change came from a server push, skip it
      if (_suppressSync) {
        _suppressSync = false;
        return;
      }
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        const run = () => syncToBackend(sessionId);
        const p = (_inflightSync ? _inflightSync.then(run) : run())
          .finally(() => { if (_inflightSync === p) _inflightSync = null; });
        _inflightSync = p;
      }, 300);
    },
    // Shallow equality: only fire when values actually change, not just references.
    // This prevents the loop where the server echoes back identical data and
    // hydrateFromSession creates new object references for the same values.
    { equalityFn: shallow },
  );

  return () => {
    isSubscribed = false;
    _currentSessionId = null;
    if (debounceTimer) clearTimeout(debounceTimer);
    unsub();
  };
}
